import { platformData } from './platform.capacitor'
import type { PlatformData } from './platform.capacitor'
import { assertNotNull } from '@/helpers/typing'
import { MEDIUM_WIDTH, MOBILE_WIDTH, TABLET_WIDTH } from '@/init/settings'

export type { PlatformData }

export interface Platform {
  _data: PlatformData | null

  init: () => Promise<PlatformData>
  deviceInfo: () => Promise<PlatformData>
  isNativeIOSApp: () => Promise<boolean>
  isNativeAndroidApp: () => Promise<boolean>
  isNativeApp: () => Promise<boolean>
  isIOS: () => Promise<boolean>
  isAndroid: () => Promise<boolean>
  isIpadWeb: () => boolean
  isMobileWidth: () => boolean
  isMediumWidth: () => boolean
  isTabletWidth: () => boolean
  isDesktopWidth: () => boolean
  isTouchScreen: () => boolean
  openUrl: (url: string) => Promise<void>
  appVersion: () => Promise<string>
  appBuild: () => Promise<number>
}

export const platform: Platform = {
  _data: null,

  async init(): Promise<PlatformData> {
    if (this._data === null) {
      await platformData.init()
      this._data = platformData
    }
    return new Promise((resolve) => {
      resolve(assertNotNull(this._data))
    })
  },

  async deviceInfo(): Promise<PlatformData> {
    const data = await this.init()
    return data
  },

  /**
   * Retruns true if current platform is native iOS app.
   */
  async isNativeIOSApp(): Promise<boolean> {
    const info = await this.deviceInfo()
    return info.platform === 'ios'
  },

  /**
   * Retruns true if current platform is native Android app.
   */
  async isNativeAndroidApp(): Promise<boolean> {
    const info = await this.deviceInfo()
    return info.platform === 'android'
  },

  /**
   * Retruns true current platform is native mobile app.
   */
  async isNativeApp(): Promise<boolean> {
    const info = await this.deviceInfo()
    return info.platform === 'android' || info.platform === 'ios'
  },

  /**
   * Returns true if the app is running on an iOS device
   * (not necessarily as a native app).
   */
  async isIOS(): Promise<boolean> {
    const info = await this.deviceInfo()
    return info._deviceInfo?.operatingSystem === 'ios'
  },

  /**
   * Returns true if the app is running on an android device
   * (not necessarily as a native app).
   */
  async isAndroid(): Promise<boolean> {
    const info = await this.deviceInfo()
    return info._deviceInfo?.operatingSystem === 'android'
  },

  /**
   * Returns true if we are running in a browser on an iPad.
   *
   * iPadOS reports itself as `MacIntel` in `navigator.platform` and does not
   * mention "iPad" in the user agent (desktop mode is the default in Safari),
   * so an iPad is detected as a "Mac" that also has a touch screen.
   */
  isIpadWeb(): boolean {
    return (
      this.isTouchScreen() &&
      navigator.maxTouchPoints > 1 &&
      /Mac/.test(navigator.platform)
    )
  },

  /**
   * Returns true if we are running on mobile (small screen).
   */
  isMobileWidth(): boolean {
    return window.innerWidth <= MOBILE_WIDTH
  },

  /**
   * Returns true if we are running on mobile with big screen (e.g. phablets and foldable phones).
   */
  isMediumWidth(): boolean {
    return window.innerWidth <= MEDIUM_WIDTH
  },

  /**
   * Returns true if we are running on tablet.
   */
  isTabletWidth(): boolean {
    return window.innerWidth > MOBILE_WIDTH && window.innerWidth <= TABLET_WIDTH
  },

  /**
   * Returns true if we are running on anything larger than a tablet (e.g. a desktop).
   */
  isDesktopWidth(): boolean {
    return window.innerWidth > TABLET_WIDTH
  },

  /**
   * Returns true if the device has a touch screen.
   */
  isTouchScreen(): boolean {
    // `hover: none` matches when the primary input
    // of the device cannot hover or cannot conveniently hover
    // (on smartphones, usually there is a hover state, but it
    // requires a long press, so it still returns `none`).
    //
    // `pointer: coarse` matches when when the primary input
    // of the device has limited accucary, such as touch screens.
    //
    // More details: https://www.w3.org/TR/mediaqueries-4/#mf-interaction
    return (
      window.matchMedia('(hover: none)').matches ||
      window.matchMedia('(pointer: coarse)').matches
    )
  },

  async openUrl(url: string): Promise<void> {
    // @ts-ignore
    await this._data.openUrl(url)
  },

  /**
   * Returns current app version.
   */
  async appVersion(): Promise<string> {
    const info = await this.deviceInfo()
    return info.appVersion
  },

  /**
   * Returns current app build.
   */
  async appBuild(): Promise<number> {
    const info = await this.deviceInfo()
    return info.appBuild
  },
}
